import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

const WAVE_REF_DURATION_MS = 3000;
const WAVE_SPREAD_SPEED = 0.5;
const WAVE_EMIT_MARGIN_PX = 24;
const WAVE_TRAVEL_RATIO = 0.58;
const WAVE_RECEDE_MS = 380;

function recedeWaveAnimations(elements) {
  const finishPromises = elements.map((element) => {
    element.getAnimations().forEach((animation) => {
      animation.cancel();
    });

    const computed = getComputedStyle(element);
    const startTransform = computed.transform;
    const startOpacity = Number(computed.opacity) || 1;

    const animation = element.animate(
      [
        { transform: startTransform, opacity: startOpacity },
        { transform: 'translateX(0)', opacity: 1 },
      ],
      {
        duration: WAVE_RECEDE_MS,
        easing: 'cubic-bezier(0.4, 0, 1, 1)',
        fill: 'forwards',
      }
    );

    return animation.finished.catch(() => undefined);
  });

  return Promise.all(finishPromises);
}

// The Winstons – "Amen, Brother" break (~136 BPM), 2-bar 16th-note grid
const AMEN_BPM = 136;
const AMEN_SIXTEENTH_MS = (60 / AMEN_BPM / 4) * 1000;
const AMEN_BAR_STEPS = 32;

const AMEN_KICK = [
  1, 0, 0, 0, 0, 0, 1, 0, 0, 1, 0, 0, 1, 0, 0, 0,
  1, 0, 0, 0, 0, 0, 1, 0, 0, 1, 0, 0, 1, 0, 0, 0,
];

const AMEN_SNARE = [
  0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0, 1, 0, 1,
  0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 1, 0, 1, 0, 0,
];

const AMEN_HAT_CLOSED = [
  1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0,
  1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0,
];

const AMEN_HAT_OPEN = [
  0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0, 0,
  0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 0,
];

const AMEN_LAYERS = [
  { sample: 'kick', pattern: AMEN_KICK },
  { sample: 'snare', pattern: AMEN_SNARE },
  { sample: 'hatClosed', pattern: AMEN_HAT_CLOSED },
  { sample: 'hatOpen', pattern: AMEN_HAT_OPEN },
];

const SAMPLE_CHARS = {
  kick: { left: '(', right: ')' },
  snare: { left: '[', right: ']' },
  hatClosed: { left: '.', right: '.' },
  hatOpen: { left: '~', right: '~' },
};

function collectWaveElements(leftLane, rightLane) {
  return [
    ...(leftLane?.querySelectorAll('.ascii-wave') ?? []),
    ...(rightLane?.querySelectorAll('.ascii-wave') ?? []),
  ];
}

function reverseWaveAnimations(elements) {
  return recedeWaveAnimations(elements);
}

function Wave({ direction, sample }) {
  const chars = SAMPLE_CHARS[sample];
  const char = direction === 'left' ? chars.left : chars.right;
  const className = [
    'ascii-wave',
    direction === 'left' ? 'ascii-wave--left' : 'ascii-wave--right',
    `ascii-wave--${sample}`,
  ].join(' ');

  return <span className={className}>{char}</span>;
}

function AudioRentalWaves({ tagline }) {
  const textRef = useRef(null);
  const leftLaneRef = useRef(null);
  const rightLaneRef = useRef(null);
  const maxDurationRef = useRef(WAVE_REF_DURATION_MS);
  const removalTimeoutsRef = useRef(new Map());
  const [laneStyle, setLaneStyle] = useState({});
  const [waves, setWaves] = useState([]);
  const [active, setActive] = useState(false);
  const [receding, setReceding] = useState(false);

  const clearRemovalTimeouts = useCallback(() => {
    removalTimeoutsRef.current.forEach((timeoutId) => {
      window.clearTimeout(timeoutId);
    });
    removalTimeoutsRef.current.clear();
  }, []);

  const finishRecede = useCallback(() => {
    setWaves([]);
    setReceding(false);
  }, []);

  const spawnWave = useCallback((sample) => {
    const id = `${Date.now()}-${Math.random()}-${sample}`;
    setWaves((prev) => [...prev, { id, sample }]);
    const timeoutId = window.setTimeout(() => {
      removalTimeoutsRef.current.delete(id);
      setWaves((prev) => prev.filter((wave) => wave.id !== id));
    }, maxDurationRef.current);
    removalTimeoutsRef.current.set(id, timeoutId);
  }, []);

  const toggleWaves = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();

    if (receding) {
      return;
    }

    if (!active) {
      setActive(true);
      return;
    }

    clearRemovalTimeouts();
    setActive(false);
    setReceding(true);
  }, [active, receding, clearRemovalTimeouts]);

  const updateLanes = useCallback(() => {
    if (!textRef.current) {
      return;
    }

    const textEl = textRef.current;
    const textRect = textEl.getBoundingClientRect();
    const fontSize = parseFloat(window.getComputedStyle(textEl).fontSize) || 14;
    const rightGlyphInset = fontSize * 0.35;

    const leftEdge = textRect.left;
    const rightEdge = textRect.right;
    const centerY = textRect.top + textRect.height / 2;

    const leftEmitX = Math.max(leftEdge - WAVE_EMIT_MARGIN_PX, 0);
    const rightEmitX = rightEdge + WAVE_EMIT_MARGIN_PX + rightGlyphInset;
    const leftLane = leftEmitX;
    const rightLane = Math.max(window.innerWidth - rightEmitX, 0);
    const leftTravel = leftLane * WAVE_TRAVEL_RATIO;
    const rightTravel = rightLane * WAVE_TRAVEL_RATIO;
    const refDist = Math.max(leftTravel, rightTravel, 1);
    const leftDurationMs = (leftTravel / refDist) * WAVE_REF_DURATION_MS / WAVE_SPREAD_SPEED;
    const rightDurationMs = (rightTravel / refDist) * WAVE_REF_DURATION_MS / WAVE_SPREAD_SPEED;
    maxDurationRef.current = Math.max(leftDurationMs, rightDurationMs);

    setLaneStyle({
      '--wave-center-y': `${centerY}px`,
      '--wave-left-width': `${leftLane}px`,
      '--wave-right-origin-x': `${rightEmitX}px`,
      '--wave-right-width': `${rightLane}px`,
      '--wave-travel-left': `${leftTravel}px`,
      '--wave-travel-right': `${rightTravel}px`,
      '--wave-duration-left': `${leftDurationMs}ms`,
      '--wave-duration-right': `${rightDurationMs}ms`,
    });
  }, []);

  useLayoutEffect(() => {
    updateLanes();

    const observer = new ResizeObserver(updateLanes);
    if (textRef.current) {
      observer.observe(textRef.current);
    }

    window.addEventListener('resize', updateLanes);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updateLanes);
    };
  }, [updateLanes, active, receding, tagline]);

  useLayoutEffect(() => {
    if (!receding) {
      return undefined;
    }

    let cancelled = false;
    const waveElements = collectWaveElements(leftLaneRef.current, rightLaneRef.current);

    if (waveElements.length === 0) {
      finishRecede();
      return undefined;
    }

    reverseWaveAnimations(waveElements).then(() => {
      if (!cancelled) {
        finishRecede();
      }
    });

    return () => {
      cancelled = true;
    };
  }, [receding, finishRecede]);

  useEffect(() => {
    if (!active) {
      return undefined;
    }

    const timeouts = new Set();

    const scheduleAmenBar = () => {
      AMEN_LAYERS.forEach(({ sample, pattern }) => {
        pattern.forEach((hit, step) => {
          if (hit) {
            const timeoutId = window.setTimeout(
              () => spawnWave(sample),
              step * AMEN_SIXTEENTH_MS
            );
            timeouts.add(timeoutId);
          }
        });
      });
    };

    scheduleAmenBar();
    const barInterval = window.setInterval(
      scheduleAmenBar,
      AMEN_BAR_STEPS * AMEN_SIXTEENTH_MS
    );

    return () => {
      window.clearInterval(barInterval);
      timeouts.forEach((timeoutId) => window.clearTimeout(timeoutId));
    };
  }, [active, spawnWave]);

  useEffect(() => () => {
    clearRemovalTimeouts();
  }, [clearRemovalTimeouts]);

  const showLanes = (active || receding) && waves.length > 0;
  const taglineClassName = [
    'logo-sub',
    active || receding ? 'logo-sub--active' : 'logo-sub--trigger',
    receding ? 'logo-sub--receding' : '',
  ].filter(Boolean).join(' ');

  return (
    <>
      <span
        ref={textRef}
        className={taglineClassName}
        role="button"
        tabIndex={0}
        aria-pressed={active}
        aria-busy={receding}
        onClick={toggleWaves}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            toggleWaves(event);
          }
        }}
      >
        {tagline}
      </span>
      {showLanes && (
        <>
          <div
            ref={leftLaneRef}
            className="wave-lane wave-lane--left"
            style={laneStyle}
            aria-hidden="true"
          >
            {waves.map((wave) => (
              <Wave key={`${wave.id}-left`} direction="left" sample={wave.sample} />
            ))}
          </div>
          <div
            ref={rightLaneRef}
            className="wave-lane wave-lane--right"
            style={laneStyle}
            aria-hidden="true"
          >
            {waves.map((wave) => (
              <Wave key={`${wave.id}-right`} direction="right" sample={wave.sample} />
            ))}
          </div>
        </>
      )}
    </>
  );
}

export default AudioRentalWaves;
